import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Form, Icon, Message, Table } from 'components/HeroSemantic';

import EntityDetailsFormField from './EntityDetailsFormField';
import ThemeButton from '../Theme/ThemeButton';

export const FIELD_TYPES = {
  inputText: 'inputText',
  dropdownList: 'dropdownList',
  status: 'status',
  textArea: 'textArea',
};

const EntityDetailsForm = ({
  children,
  cleanSubmit,
  columns,
  confirmationTitle,
  data,
  displayTopUpdateButton,
  enableUpdateView,
  fields,
  labelIds,
  showFullDetailsConfirmation,
  updateViewNotes,
  warningMessage,
  onCancel,
  onChange,
  onSave,
}) => {
  const { formatMessage } = useIntl();
  const [isConfirmView, setIsConfirmView] = useState(false);
  const [updateView, setUpdateView] = useState(false);
  const [updatedValues, setUpdatedValues] = useState({});

  const formData = useMemo(() => ({ ...data, ...updatedValues }), [data, updatedValues]);

  const isDirty = Object.keys(updatedValues).length > 0;

  const hasRequiredMissing = fields.some(f => f.required && !formData[f.name]);
  // Block submit when any field reports an explicit validation failure
  // (`failed === true`). Optimistic states (`null`/`undefined`) and loading
  // states don't gate submission.
  const hasInvalid = fields.some(f => f?.validation?.failed === true);

  const isUpdateView = updateView || enableUpdateView;
  const updateButtonVisible = !isUpdateView && !isConfirmView && typeof onSave === 'function';

  // All the specified data fields are displayed in the Read and Edit views
  // The confirmation view will only display the data fields that have been modified
  const displayFields = !isConfirmView || showFullDetailsConfirmation ? fields : Object.keys(updatedValues).map(key => fields.find(f => f.name === key));

  const resetForm = () => {
    setIsConfirmView(false);
    setUpdateView(false);
    setUpdatedValues({});
  };

  useEffect(() => {
    // Validate all fields when switching to the edition view
    if (isUpdateView) {
      fields.forEach(field => {
        if (typeof field?.validation?.validator === 'function') {
          field.validation.validator(formData[field.name]);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateView]);

  useEffect(() => {
    if (isUpdateView || isConfirmView) {
      resetForm();
    }
  // We want to reset from updated details to the read view if the ID was changed (it means a new instance was passed in)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.id]);

  const handleBlur = field => {
    if (typeof field?.validation?.validator === 'function') {
      field.validation.validator(formData[field.name]);
    }
  };

  const handleClick = (field, event) => {
    if (typeof field.onClick === 'function') {
      field.onClick(event);
    }
  };

  const handleChange = (field, value) => {
    setUpdatedValues(prevValues => {
      // 1. Copy previous values.
      const next = { ...prevValues };

      // 2. Set the changed field.
      next[field.name] = value;

      // 3. Remove any field from the update list if its value matches the original data.
      // eg.. if the original data was { 'currency': 'JPY' } and the user changed it to 'EUR'
      // the loop below will remove 'currency' from the updatedValues.
      Object.keys(next).forEach(k => {
        if (Object.is(next[k], data?.[k])) {delete next[k];}
      });

      // 4. Return the new object.
      // return { 'currency': 'EUR' } and it will be updated in the form data.
      return next;
    });
    if (typeof field.onChange === 'function') {
      field.onChange(value);
    }
  };

  const handleCancelClicked = () => {
    resetForm();
    if (typeof onCancel === 'function') {
      onCancel();
    }
  };

  const handleSubmitClicked = () => {
    setUpdateView(false);
    setIsConfirmView(true);
    if (typeof onChange === 'function') {
      onChange({ id: data.id, ...updatedValues });
    }
  };

  const handleSubmitConfirmedClicked = () => {
    onSave({ id: data.id, ...updatedValues });
    resetForm();
  };

  const handleUpdateClicked = () => {
    setUpdateView(true);
  };

  // Render the whole form
  return (
    <Form>
      {
        isConfirmView && !!confirmationTitle && (
          <div style={{ color: 'var(--owner-text)', fontSize: '18px', fontWeight: 700, lineHeight: '26px', marginBottom: '10px', padding: '10px' }}>
            {formatMessage({ id: 'page.user.myDetails.updateMessage' })}
          </div>
        )
      }
      {columns && columns > 1 ? (
        <>
          {updateButtonVisible && displayTopUpdateButton && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0 auto 16px', maxWidth: '1110px' }}>
              <button
                type="button"
                onClick={handleUpdateClicked}
                className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--tenant-primary)] hover:bg-[var(--owner-accent-subtle)] hover:border-[var(--owner-accent-border)] hover:text-[var(--owner-accent-readable)]"
                style={{
                  alignItems: 'center',
                  background: 'transparent',
                  border: '1px solid var(--owner-border-strong)',
                  borderRadius: '8px',
                  color: 'var(--owner-text)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  fontSize: '13px',
                  fontWeight: 600,
                  gap: '6px',
                  lineHeight: '18px',
                  padding: '7px 14px',
                  transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
                }}
              >
                <Icon name="pencil" style={{ width: '14px', height: '14px' }} />
                {formatMessage({ id: 'general.updateDetails' })}
              </button>
            </div>
          )}
          <div
            className={[
              'grid grid-cols-1',
              columns === 2 ? 'md:grid-cols-2' : '',
              columns === 3 ? 'md:grid-cols-2 lg:grid-cols-3' : '',
              columns >= 4 ? 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : '',
            ].filter(Boolean).join(' ')}
            style={{
              gap: '20px 28px',
              margin: isConfirmView ? '10px' : '0 auto',
              maxWidth: '1110px',
            }}
          >
            {
              displayFields.map(field => (
                <EntityDetailsFormField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...field}
                  key={field.name}
                  compact
                  readOnly={!isUpdateView || !!field.readOnly}
                  value={formData[field.name]}
                  onBlur={() => handleBlur(field)}
                  onClick={event => handleClick(field, event)}
                  onChange={value => handleChange(field, value)}
                />
              ))
            }
          </div>
        </>
      ) : (
        <>
          {updateButtonVisible && displayTopUpdateButton && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0 auto 14px', maxWidth: '1110px' }}>
              <button
                type="button"
                onClick={handleUpdateClicked}
                className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--tenant-primary)] hover:bg-[var(--owner-accent-subtle)] hover:border-[var(--owner-accent-border)] hover:text-[var(--owner-accent-readable)]"
                style={{
                  alignItems: 'center',
                  background: 'transparent',
                  border: '1px solid var(--owner-border-strong)',
                  borderRadius: '8px',
                  color: 'var(--owner-text)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  fontSize: '13px',
                  fontWeight: 600,
                  gap: '6px',
                  lineHeight: '18px',
                  padding: '7px 14px',
                  transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
                }}
              >
                <Icon name="pencil" style={{ width: '14px', height: '14px' }} />
                {formatMessage({ id: 'general.updateDetails' })}
              </button>
            </div>
          )}
          <Table
            basic="very"
            compact={isUpdateView}
            style={
              isConfirmView ? { padding: '10px' } : { margin: '0 auto', maxWidth: '1110px' }
            }
          >
            <Table.Body>
              {
                displayFields.map(field => (
                  <EntityDetailsFormField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...field}
                    key={field.name}
                    readOnly={!isUpdateView || !!field.readOnly}
                    value={formData[field.name]}
                    onBlur={() => handleBlur(field)}
                    onClick={event => handleClick(field, event)}
                    onChange={value => handleChange(field, value)}
                  />
                ))
              }
            </Table.Body>
          </Table>
        </>
      )}
      {
        children
      }
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '16px' }}>
        {/* Update Button */}
        {
          updateButtonVisible && (
            <ThemeButton
              titleId={labelIds && labelIds.buttons && labelIds.buttons.update ? labelIds.buttons.update : 'general.update'}
              onClick={handleUpdateClicked}
            />
          )
        }
        {/* Cancel Button */}
        {
          (isUpdateView || isConfirmView) && (
            <ThemeButton
              titleId={labelIds && labelIds.buttons && labelIds.buttons.cancel ? labelIds.buttons.cancel : 'general.cancel'}
              onClick={handleCancelClicked}
              style={{ background: 'var(--owner-surface)', border: '1px solid var(--owner-border-strong)', color: 'var(--owner-text)' }}
            />
          )
        }
        {/* Save Button */}
        {
          isUpdateView && !isConfirmView && (
            <ThemeButton
              disabled={(!isDirty && !cleanSubmit) || (isDirty && hasRequiredMissing) || hasRequiredMissing || hasInvalid}
              titleId={labelIds && labelIds.buttons && labelIds.buttons.submit ? labelIds.buttons.submit : 'general.submit'}
              onClick={handleSubmitClicked}
              style={{ background: '#16A34A', color: '#FFFFFF' }}
            />
          )
        }
        {/* Confirm Save Button */}
        {
          isConfirmView && (
            <ThemeButton
              titleId={labelIds && labelIds.buttons && labelIds.buttons.confirm ? labelIds.buttons.confirm : 'general.request'}
              onClick={handleSubmitConfirmedClicked}
              style={{ background: '#16A34A', color: '#FFFFFF' }}
            />
          )
        }
      </div>
      {/* Update View Notes */}
      {
        isUpdateView && (
          updateViewNotes.map(note => (
            <div style={{ color: 'var(--owner-danger)', fontSize: '14px', lineHeight: '22px', marginTop: '1em' }}>
              {note}
            </div>
          ))
        )
      }
      {/* Confirmation-view warning. Rendered as a left-aligned alert with the
          icon vertically aligned with the first line of text, taking advantage
          of the Message component's warning tone (amber border + tinted bg)
          without the prior centered/bold treatment that was hard to read. */}
      {
        isConfirmView && warningMessage && (
          <Message warning className="mt-3" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', textAlign: 'left' }}>
            <Icon
              name="warning circle"
              style={{ color: '#b45309', flexShrink: 0, marginTop: '2px' }}
            />
            <div style={{ color: '#78350f', fontSize: '14px', lineHeight: '20px' }}>
              {warningMessage}
            </div>
          </Message>
        )
      }
    </Form>
  );
};

EntityDetailsForm.propTypes = {
  children: PropTypes.node,
  // cleanSubmit: tells if values can be submitted even without any applied change (when form is not dirty)
  cleanSubmit: PropTypes.bool,
  // columns: when > 1, render fields in a responsive CSS grid (compact layout)
  // instead of the default one-row-per-field Table layout.
  columns: PropTypes.number,
  confirmationTitle: PropTypes.string,
  data: PropTypes.object,
  displayTopUpdateButton: PropTypes.bool,
  enableUpdateView: PropTypes.bool,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      noteSymbol: PropTypes.string,
      translationId: PropTypes.string.isRequired,
      validation: PropTypes.shape({
        failed: PropTypes.bool,
        loading: PropTypes.bool,
        success: PropTypes.bool,
        validator: PropTypes.func,
      }),
    }).isRequired
  ).isRequired,
  labelIds: PropTypes.shape({
    buttons: {
      cancel: PropTypes.string,
      confirm: PropTypes.string,
      submit: PropTypes.string,
      update: PropTypes.string,
    },
  }),
  showFullDetailsConfirmation: PropTypes.bool,
  warningMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  updateViewNotes: PropTypes.arrayOf(PropTypes.string),
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
};

EntityDetailsForm.defaultProps = {
  cleanSubmit: false,
  data: {},
  displayTopUpdateButton: false,
  enableUpdateView: false,
  labelIds: {},
  updateViewNotes: [],
  showFullDetailsConfirmation: false,
};

export default EntityDetailsForm;
